import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { sanitizeSearchInput, sanitizePagination } from '@/utils/searchUtils';
import { getCurrentUserRole } from './companyFilterHelper';
import type { PaginatedData } from './behavioralService';
import type { FilterState } from '@/types/filterTypes';

/**
 * Fetch candidates with combined behavioral and cognitive data using the database view
 * This reduces API calls by joining the data server-side
 */
export async function getCandidateAssessments(
  page: number,
  pageSize: number,
  searchTerm?: string,
  filters?: FilterState,
  activeCompanyId?: string
): Promise<PaginatedData<any>> {
  logger.info('getCandidateAssessments: Using optimized view query');
  
  const { page: safePage, pageSize: safePageSize } = sanitizePagination(page, pageSize);
  const { isSuperAdmin, companyId } = await getCurrentUserRole(activeCompanyId);

  let query = supabase
    .from('candidate_assessments')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false });

  // Apply company filtering
  if (activeCompanyId) {
    query = query.eq('company_id', activeCompanyId);
  } else if (!isSuperAdmin && companyId) {
    query = query.eq('company_id', companyId);
  }

  // Apply sanitized search filter
  if (searchTerm) {
    const sanitized = sanitizeSearchInput(searchTerm);
    if (sanitized) {
      query = query.or(`full_name.ilike.%${sanitized}%,email.ilike.%${sanitized}%`);
    }
  }

  // Apply behavioral trait filters
  if (filters) {
    query = applyBehavioralFilters(query, filters);
  }

  // Fetch with standard pagination
  const startIndex = (safePage - 1) * safePageSize;
  const { data, error, count } = await query.range(startIndex, startIndex + safePageSize - 1);

  if (error) {
    logger.error('Error fetching candidate assessments:', error);
    throw error;
  }

  // Deduplicate as safety net, but use Supabase's exact count for pagination
  const deduplicatedData = deduplicateByEmail(data || []);

  return {
    data: deduplicatedData,
    count: count || 0,
  };
}

function applyBehavioralFilters(query: any, filters: FilterState): any {
  // Trait range filters
  const traitKeys = ['dominance', 'extraversion', 'patience', 'formality', 'objectivity'] as const;
  for (const key of traitKeys) {
    const range = filters[key];
    if (range.min > 0 || range.max < 100) {
      query = query
        .gte(`scores->${key}`, range.min)
        .lte(`scores->${key}`, range.max);
    }
  }

  // Stage (status) filter
  if (filters.status && filters.status.length > 0) {
    if (filters.statusMode === 'exclude') {
      query = query.not('status', 'in', `(${filters.status.map((s: string) => `"${s}"`).join(',')})`);
    } else {
      query = query.in('status', filters.status);
    }
  }

  return query;
}

function deduplicateByEmail(data: any[]): any[] {
  return data.reduce((acc: any[], item) => {
    const existingIndex = acc.findIndex(s => s.email === item.email);
    const scores = item.scores as Record<string, any> | null;
    
    if (existingIndex === -1) {
      acc.push({
        ...item,
        scores: {
          ...(scores || {}),
          workBehaviorHistory: scores?.workBehaviorHistory || []
        }
      });
    } else {
      const existing = acc[existingIndex];
      if (scores?.workBehavior) {
        existing.scores.workBehaviorHistory = existing.scores.workBehaviorHistory || [];
        existing.scores.workBehaviorHistory.push({
          ...scores.workBehavior,
          completedAt: item.completed_at
        });
      }
    }
    
    return acc;
  }, []);
}
